import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react';

import { PaymentError } from '../core/payment-error';
import { PaymentButtonState } from '../types/config';

interface PaymentStateContextValue {
    paymentState: PaymentButtonState;
    setProcessing: (isProcessing: boolean) => void;
    setCanSubmit: (canSubmit: boolean) => void;
    setPaymentComplete: (paymentComplete: boolean) => void;
    getPaymentState: () => PaymentButtonState;
    triggerSubmit: () => Promise<void>;
    registerSubmitHandler: (handler: () => Promise<void>) => void;
}

const PaymentStateContext = createContext<PaymentStateContextValue | undefined>(
    undefined
);

interface PaymentStateProviderProps {
    children: React.ReactNode;
    onPaymentStateChange?: (state: PaymentButtonState) => void;
}

export const PaymentStateProvider: React.FC<PaymentStateProviderProps> = ({
    children,
    onPaymentStateChange
}) => {
    const [isProcessing, setIsProcessing] = useState(false);
    const [canSubmit, setCanSubmit] = useState(false);
    const [paymentComplete, setIsPaymentComplete] = useState(false);

    const submitHandlerRef = useRef<(() => Promise<void>) | null>(null);

    const paymentState = useMemo<PaymentButtonState>(
        () => ({
            isProcessing,
            canSubmit,
            paymentComplete
        }),
        [isProcessing, canSubmit, paymentComplete]
    );

    useEffect(() => {
        onPaymentStateChange?.(paymentState);
    }, [paymentState, onPaymentStateChange]);

    const setProcessing = useCallback((processing: boolean) => {
        setIsProcessing(processing);
    }, []);

    const setCanSubmitState = useCallback((canSubmitValue: boolean) => {
        setCanSubmit(canSubmitValue);
    }, []);

    const setPaymentComplete = useCallback((value: boolean) => {
        setIsPaymentComplete(value);
    }, []);

    const getPaymentState = useCallback(() => paymentState, [paymentState]);

    const registerSubmitHandler = useCallback(
        (handler: () => Promise<void>) => {
            submitHandlerRef.current = handler;
        },
        []
    );

    const triggerSubmit = useCallback(async () => {
        if (!submitHandlerRef.current) {
            throw new PaymentError(
                'No payment form loaded. Please ensure a payment method is selected before attempting to submit.',
                'VALIDATION_ERROR'
            );
        }

        if (isProcessing) {
            throw new PaymentError(
                'Cannot submit payment: payment is currently being processed',
                'VALIDATION_ERROR'
            );
        }

        if (!canSubmit) {
            throw new PaymentError(
                'Cannot submit payment: form is not ready',
                'VALIDATION_ERROR'
            );
        }

        await submitHandlerRef.current();
    }, [isProcessing, canSubmit]);

    const value = useMemo<PaymentStateContextValue>(
        () => ({
            paymentState,
            setProcessing,
            setCanSubmit: setCanSubmitState,
            setPaymentComplete,
            getPaymentState,
            triggerSubmit,
            registerSubmitHandler
        }),
        [
            paymentState,
            setProcessing,
            setCanSubmitState,
            setPaymentComplete,
            getPaymentState,
            triggerSubmit,
            registerSubmitHandler
        ]
    );

    return (
        <PaymentStateContext.Provider value={value}>
            {children}
        </PaymentStateContext.Provider>
    );
};

export const usePaymentState = (): PaymentStateContextValue => {
    const context = useContext(PaymentStateContext);
    if (!context) {
        throw new PaymentError(
            'usePaymentState must be used within PaymentStateProvider',
            'VALIDATION_ERROR'
        );
    }
    return context;
};
